import { Delta } from 'quill/core';
import Logger from '../utils/Logger';

interface Commit {
  baseVersion: number;
  baseChecksum: number;
  delta: Delta;
  clientId: string;
}

interface SyncRequest {
  baseVersion: number;
}

interface SyncResponse {
  revisions: Revision[];
}

interface Revision {
  version: number;
  delta: Delta;
  clientId: string;
  checksum: number;
}

interface Config {
  initialVersion: number;
  initialChecksum: number;
  sendCommit(commit: Commit): void;
  sendSync(sync: SyncRequest): Promise<SyncResponse>;
  onRemoteDelta(delta: Delta): void;
  onStateChange(state: SyncEngineState): void;
  onSyncStatusChange(status: Status): void;
}

interface SyncEngineState {
  version: number;
  unsaved: Delta;
}

type Status = 'down' | 'up' | 'syncing';

const MIN_RETRY_MS = 10000;
const COMMIT_TIMEOUT_MS = 10000;

export default class SyncEngine {
  status: Status = 'down';
  version: number;
  checksum: number;
  private syncAttempt = 0;
  private queued = new Delta();
  private committing: Delta | null = null;
  private unsaved = new Delta();
  private readonly sendCommit: Config['sendCommit'];
  private readonly sendSync: Config['sendSync'];
  private readonly onRemoteDelta: Config['onRemoteDelta'];
  private readonly onStateChange: Config['onStateChange'];
  private readonly onSyncStatusChange: Config['onSyncStatusChange'];
  private readonly bufferedUpdates = new Map<number, Revision>();
  private readonly clientId = Math.random().toString(36).substring(2, 10);
  private commitTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(config: Config) {
    this.version = config.initialVersion;
    this.checksum = config.initialChecksum;
    this.sendCommit = config.sendCommit.bind(null);
    this.sendSync = config.sendSync.bind(null);
    this.onRemoteDelta = config.onRemoteDelta.bind(null);
    this.onStateChange = config.onStateChange.bind(null);
    this.onSyncStatusChange = config.onSyncStatusChange.bind(null);
  }

  isCommitting(): boolean {
    return this.committing !== null;
  }

  setIsConnected(isConnected: boolean): void {
    if (isConnected === (this.status !== 'down')) {
      return;
    }

    if (isConnected) {
      // don't setStatus('up') because that would just fire an extra
      // status change before status becomes 'syncing'.
      this.startSyncing(true);
    } else {
      this.setStatus('down');
      this.cancelCommitTimer();
    }
  }

  addLocalDelta(delta: Delta): void {
    this.queued = this.queued.compose(delta);
    this.updatePublicState({
      unsaved: this.unsaved.compose(delta),
    });
    this.maybeCommit();
  }

  receiveUpdate(revision: Revision): void {
    if (this.status === 'syncing') {
      this.bufferedUpdates.set(revision.version, revision);
      return;
    }
    if (revision.version !== this.version + 1) {
      this.startSyncing();
      return;
    }
    this.handleUpdate(revision);
  }

  getSyncState(): SyncEngineState {
    return {
      version: this.version,
      unsaved: this.unsaved,
    };
  }

  private handleUpdate(revision: Revision): void {
    if (this.checksum + revision.delta.changeLength() !== revision.checksum) {
      Logger.error('Checksum mismatch in update', {
        oldChecksum: this.checksum,
        newChecksum: revision.checksum,
        delta: revision.delta,
        version: revision.version,
      });
    }
    if (this.committing !== null && revision.clientId === this.clientId) {
      // commit acked
      this.cancelCommitTimer();
      this.committing = null;
      this.checksum = revision.checksum;
      this.updatePublicState({
        version: this.version + 1,
        unsaved: this.queued,
      });
      this.maybeCommit();
    } else {
      // received remote update
      const originalDelta = revision.delta;
      const transformedDelta =
        this.committing !== null
          ? this.committing.transform(originalDelta, false)
          : originalDelta;
      const remoteDelta = this.queued.transform(transformedDelta, false);

      if (this.committing !== null) {
        this.committing = originalDelta.transform(this.committing, true);
      }
      this.queued = transformedDelta.transform(this.queued, true);
      this.checksum = revision.checksum;
      this.updatePublicState({
        version: this.version + 1,
      });
      if (remoteDelta.length() > 0) {
        this.onRemoteDelta(remoteDelta);
      }
    }
  }

  private startSyncing(skipUpCheck = false): void {
    if (!skipUpCheck && this.status !== 'up') {
      return;
    }

    this.setStatus('syncing');
    this.cancelCommitTimer();
    this.doSync();
  }

  private doSync(): void {
    // caller ensures status === 'syncing'.
    // incrementing this.syncAttempt effectively cancels any outstanding syncs
    this.syncAttempt += 1;
    const attempt = this.syncAttempt;
    this.bufferedUpdates.clear();
    const sendTime = Date.now();
    this.sendSync({ baseVersion: this.version })
      .then(({ revisions }) => {
        if (this.status !== 'syncing' || this.syncAttempt !== attempt) {
          return;
        }
        revisions.forEach((revision) => {
          if (revision.version !== this.version + 1) {
            Logger.error('Unexpected version in sync response', {
              baseVersion: this.version,
              revision,
            });
          }
          this.handleUpdate(revision);
        });
        // eslint-disable-next-line no-constant-condition
        while (true) {
          const update = this.bufferedUpdates.get(this.version + 1);
          if (update) {
            this.handleUpdate(update);
          } else {
            break;
          }
        }
        this.bufferedUpdates.clear();
        this.abortCommit();
        this.setStatus('up');
        this.maybeCommit();
      })
      .catch(() => {
        setTimeout(
          () => {
            if (this.status !== 'syncing' || this.syncAttempt !== attempt) {
              return;
            }
            this.doSync();
          },
          sendTime + MIN_RETRY_MS - Date.now(),
        );
      });
  }

  private maybeCommit(): void {
    if (
      !(
        this.status === 'up' &&
        this.committing === null &&
        this.queued.length() !== 0
      )
    ) {
      return;
    }
    this.committing = this.queued;
    this.queued = new Delta();
    this.sendCommit({
      baseVersion: this.version,
      baseChecksum: this.checksum,
      delta: this.committing,
      clientId: this.clientId,
    });
    this.commitTimer = setTimeout(() => {
      this.startSyncing();
    }, COMMIT_TIMEOUT_MS);
  }

  private abortCommit(): void {
    if (this.committing !== null) {
      this.queued = this.committing.compose(this.queued);
      this.committing = null;
      // unsaved and version remain the same
    }
  }

  private cancelCommitTimer(): void {
    if (this.commitTimer !== null) {
      clearTimeout(this.commitTimer);
      this.commitTimer = null;
    }
  }

  private updatePublicState(updates: Partial<SyncEngineState>) {
    const { version, unsaved } = updates;
    this.version = version !== undefined ? version : this.version;
    this.unsaved = unsaved !== undefined ? unsaved : this.unsaved;
    this.onStateChange(this.getSyncState());
  }

  private setStatus(status: Status) {
    if (this.status !== status) {
      this.status = status;
      this.onSyncStatusChange(status);
    }
  }
}
